/**
 * Plays a classic ringback tone (dual-tone, on/off pattern) while waiting for the
 * customer to answer a support call. Uses Web Audio API — no audio file required.
 */

export const RING_CYCLE_MS = 3000;
/** Sales/Support local ringback only. CSR call-ring uses CLIENT_RING_TIMEOUT_MS. */
export const DEFAULT_MAX_RING_BELLS = 8;
/** Canonical client ring window. Matches native IncomingCallNotifier.RING_TIMEOUT_MS. */
export const CLIENT_RING_TIMEOUT_MS = 60_000;

export function ringBellsForClientTimeout(ms = CLIENT_RING_TIMEOUT_MS) {
  return Math.max(1, Math.ceil(ms / RING_CYCLE_MS));
}

let audioContext = null;
let burstTimeoutId = null;
let cycleTimeoutId = null;
let stopped = true;
let ringCount = 0;
let activeOptions = null;

function getContext() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioContext || audioContext.state === "closed") {
    audioContext = new Ctx();
  }
  return audioContext;
}

function playBurst(ctx) {
  if (stopped || !ctx) return;

  const gain = ctx.createGain();
  gain.gain.value = 0.12;
  gain.connect(ctx.destination);

  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  osc1.frequency.value = 440;
  osc2.frequency.value = 480;
  osc1.connect(gain);
  osc2.connect(gain);

  const now = ctx.currentTime;
  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + 1);
  osc2.stop(now + 1);

  burstTimeoutId = window.setTimeout(() => {
    osc1.disconnect();
    osc2.disconnect();
    gain.disconnect();
  }, 1100);
}

function scheduleCycle(ctx) {
  if (stopped) return;

  ringCount += 1;
  const { maxRings, onRing, onComplete } = activeOptions || {};
  onRing?.(ringCount, maxRings ?? DEFAULT_MAX_RING_BELLS);

  playBurst(ctx);

  const limit = maxRings ?? DEFAULT_MAX_RING_BELLS;
  if (limit > 0 && ringCount >= limit) {
    cycleTimeoutId = window.setTimeout(() => {
      if (!stopped) {
        const complete = onComplete;
        stopSupportCallRingback();
        complete?.();
      }
    }, 200);
    return;
  }

  cycleTimeoutId = window.setTimeout(() => {
    if (!stopped) scheduleCycle(ctx);
  }, RING_CYCLE_MS);
}

/**
 * @param {{ maxRings?: number, onRing?: (count: number, max: number) => void, onComplete?: () => void }} [options]
 */
export async function startSupportCallRingback(options = {}) {
  stopSupportCallRingback();
  stopped = false;
  ringCount = 0;
  activeOptions = {
    maxRings: options.maxRings ?? DEFAULT_MAX_RING_BELLS,
    onRing: options.onRing,
    onComplete: options.onComplete,
  };

  const ctx = getContext();
  if (!ctx) return;

  try {
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
  } catch {
    // ignore — browser may block until user gesture (call button click is enough)
  }

  scheduleCycle(ctx);
}

export function stopSupportCallRingback() {
  stopped = true;
  ringCount = 0;
  activeOptions = null;
  if (burstTimeoutId != null) {
    clearTimeout(burstTimeoutId);
    burstTimeoutId = null;
  }
  if (cycleTimeoutId != null) {
    clearTimeout(cycleTimeoutId);
    cycleTimeoutId = null;
  }
  if (audioContext && audioContext.state !== "closed") {
    try {
      audioContext.suspend();
    } catch {
      // ignore
    }
  }
}

export function disposeSupportCallRingback() {
  stopSupportCallRingback();
  if (audioContext) {
    try {
      audioContext.close();
    } catch {
      // ignore
    }
    audioContext = null;
  }
}
